import { module as BaseModule } from "modujs";
import * as PIXI from "pixi.js";
import ImageSizer from "../utils/ImageSizer";
import Quadtree from "../utils/Quadtree";

export default class extends BaseModule {
  constructor(m) {
    super(m);
    this.app = null;
    this.container = null;
    this.sprites = [];
    this.loader = null;
    this.quadtree = null;
    this.quadtreeBounds = null;
    this.imageSizer = new ImageSizer(window.innerWidth);
    this.draggedSprite = null;
    this.dragOffset = { x: 0, y: 0 };
    this.zIndexCounter = 1000;
    // Bind drag methods
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.wasDragging = false;
    this.dragStartPos = { x: 0, y: 0 };
    this.handleResize = this.handleResize.bind(this);
    this.resizeTimeout = null;
  }

  async init() {
    this.loader = this.el.querySelector("[data-loader]");
    await this.setupPixi();
    const imgElements = this.el.querySelectorAll("img");
    this.imgElements = imgElements; // Store for later use
    this.showLoader(0, imgElements.length);
    this.loadImages(imgElements);
    window.addEventListener('resize', this.handleResize);
  }

  async setupPixi() {
    this.app = new PIXI.Application();
    await this.app.init({
      width: this.el.offsetWidth,
      height: this.el.offsetHeight,
      backgroundColor: 0x000000,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
      antialias: true,
    });
    this.el.appendChild(this.app.canvas);
    this.container = new PIXI.Container();
    this.app.stage.addChild(this.container);
    this.quadtreeBounds = {
      x: 0,
      y: 0,
      width: this.el.offsetWidth,
      height: this.el.offsetHeight,
    };
    this.quadtree = new Quadtree(this.quadtreeBounds, 8);
  }

  async loadImages(imgElements) {
    const elements = Array.from(imgElements);
    const total = elements.length;
    let loaded = 0;
    const sprites = [];
    for (let i = 0; i < total; i++) {
      const img = elements[i];
      try {
        const texture = await PIXI.Assets.load(img.src);
        const sprite = this.createSprite(texture, img, i);
        sprites.push(sprite);
        loaded++;
        this.showLoader(loaded, total);
      } catch (e) {
        // skip failed image
        loaded++;
        this.showLoader(loaded, total);
      }
    }
    this.placeSprites(sprites);
    this.hideLoader();
  }

  showLoader(loaded, total) {
    if (this.loader) {
      const loaderText = this.loader.querySelector('[data-loader-text]');
      if (loaderText) {
        loaderText.textContent = `Loading ${Math.round((loaded / total) * 100)}%`;
      }
      this.loader.style.display = '';
    }
  }

  hideLoader() {
    if (this.loader) {
      this.loader.remove();
      this.loader = null;
    }
  }

  createSprite(texture, img, index) {
    const size = this.imageSizer.calculateSize(img.naturalWidth, img.naturalHeight);
    const sprite = new PIXI.Sprite(texture);
    sprite.width = size.width;
    sprite.height = size.height;
    sprite.anchor.set(0.5);
    sprite.alpha = 1;
    sprite.zIndex = index;
    sprite.projectLink = img.dataset.project;
    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    // Drag events
    sprite.on('pointerdown', this.onDragStart);
    sprite.on('pointerup', this.onDragEnd);
    sprite.on('pointerupoutside', this.onDragEnd);
    sprite.on('pointermove', this.onDragMove);
    // Click navigation
    sprite.on('pointertap', () => {
      if (sprite.projectLink && !this.wasDragging) this.navigateTo(sprite.projectLink);
    });
    return sprite;
  }

  placeSprites(sprites) {
    this.sprites = [];
    this.container.removeChildren();
    this.quadtree.clear();
    const w = this.el.offsetWidth;
    const h = this.el.offsetHeight;
    const sizes = sprites.map(s => ({ width: s.width, height: s.height }));

    // Calculate edgeCount as in edgeFirstPositions
    const perimeter = 2 * (w + h);
    // Use average image size for spacing
    const avgSizeObj = this.imageSizer.getAverageSize();
    const minSpacing = avgSizeObj.width; // Use width as spacing
    const edgeCount = Math.min(sprites.length, Math.max(1, Math.floor(perimeter / minSpacing)));
    console.log(`[AllOverV3] edgeCount: ${edgeCount} (minSpacing: ${minSpacing}, avgWidth: ${avgSizeObj.width.toFixed(2)}px, avgHeight: ${avgSizeObj.height.toFixed(2)}, perimeter: ${perimeter}, total: ${sprites.length})`);
    // If you want to use average image size for spacing, uncomment above and adjust minSpacing

    // Precompute positions
    const edgePositions = this.edgeFirstPositions(edgeCount, w, h, sizes.slice(0, edgeCount));
    const scatteredPositions = this.scatteredPositions(sprites.length - edgeCount, w, h, sizes.slice(edgeCount));

    for (let i = 0; i < sprites.length; i++) {
      const sprite = sprites[i];
      let pos;
      if (i < edgeCount) {
        pos = edgePositions[i];
      } else {
        pos = scatteredPositions[i - edgeCount];
      }
      sprite.x = pos.x;
      sprite.y = pos.y;
      sprite.zIndex = i;
      this.container.addChild(sprite);
      this.sprites.push(sprite);
    }

    // Rebuild quadtree
    this.quadtree.clear();
    this.sprites.forEach(sprite => {
      this.quadtree.insert({
        x: sprite.x - sprite.width / 2,
        y: sprite.y - sprite.height / 2,
        width: sprite.width,
        height: sprite.height,
        sprite,
      });
    });
    this.container.sortChildren();
  }

  edgeFirstPositions(count, w, h, sizes) {
    // Place as many images as fit around the edge, then fill inside with spiral
    const positions = [];
    if (count === 1) {
      // Only one image, center it
      positions.push({ x: w / 2, y: h / 2 });
      return positions;
    }
    // Estimate how many fit around the edge (one per 40px of perimeter, at least 1)
    const perimeter = 2 * (w + h);
    const minSpacing = 40;
    const edgeCount = Math.min(count, Math.max(1, Math.floor(perimeter / minSpacing)));
    let step = perimeter / edgeCount;
    let pos = 0;
    for (let i = 0; i < edgeCount; i++) {
      let x, y;
      if (pos < w) {
        // Top edge
        x = pos;
        y = 0;
      } else if (pos < w + h) {
        // Right edge
        x = w;
        y = pos - w;
      } else if (pos < 2 * w + h) {
        // Bottom edge
        x = w - (pos - (w + h));
        y = h;
      } else {
        // Left edge
        x = 0;
        y = h - (pos - (2 * w + h));
      }
      // Clamp to keep inside canvas (account for sprite size if provided)
      const spriteW = sizes && sizes[i] ? sizes[i].width : 0;
      const spriteH = sizes && sizes[i] ? sizes[i].height : 0;
      x = Math.max(spriteW / 2, Math.min(w - spriteW / 2, x));
      y = Math.max(spriteH / 2, Math.min(h - spriteH / 2, y));
      positions.push({ x, y });
      pos += step;
    }
    // Place the rest in a spiral inside the edge
    if (count > edgeCount) {
      const centerX = w / 2;
      const centerY = h / 2;
      let angle = 0;
      // Start spiral radius at 0 to fill the center
      let radius = 0;
      const angleStep = Math.PI / 6; // 30 degrees
      // Spread spiral images more evenly
      const radiusStep = (Math.min(w, h) * 0.35) / (count - edgeCount + 1);
      for (let i = edgeCount; i < count; i++) {
        const spriteW = sizes && sizes[i] ? sizes[i].width : 0;
        const spriteH = sizes && sizes[i] ? sizes[i].height : 0;
        const x = Math.max(spriteW / 2, Math.min(w - spriteW / 2, centerX + radius * Math.cos(angle)));
        const y = Math.max(spriteH / 2, Math.min(h - spriteH / 2, centerY + radius * Math.sin(angle)));
        positions.push({ x, y });
        angle += angleStep;
        radius += radiusStep;
      }
    }
    return positions;
  }

  scatteredPositions(count, w, h, sizes) {
    const positions = [];
    for (let i = 0; i < count; i++) {
      const spriteW = sizes && sizes[i] ? sizes[i].width : 0;
      const spriteH = sizes && sizes[i] ? sizes[i].height : 0;
      // Random position, clamped so the image stays fully visible
      const x = Math.random() * (w - spriteW) + spriteW / 2;
      const y = Math.random() * (h - spriteH) + spriteH / 2;
      positions.push({ x, y });
    }
    return positions;
  }

  onDragStart(event) {
    const sprite = event.currentTarget;
    this.draggedSprite = sprite;
    // Bring to top
    sprite.zIndex = this.zIndexCounter++;
    this.container.sortChildren();
    // Calculate offset
    this.dragOffset.x = sprite.x - event.global.x;
    this.dragOffset.y = sprite.y - event.global.y;
    sprite.cursor = 'grabbing';
    this.wasDragging = false;
    this.dragStartPos = { x: event.global.x, y: event.global.y };
  }

  onDragMove(event) {
    if (!this.draggedSprite) return;
    // Only move if pointer is down
    if (event.buttons === 1 || event.data && event.data.buttons === 1) {
      this.draggedSprite.x = event.global.x + this.dragOffset.x;
      this.draggedSprite.y = event.global.y + this.dragOffset.y;
      // If moved more than a few pixels, consider as drag
      const dx = event.global.x - this.dragStartPos.x;
      const dy = event.global.y - this.dragStartPos.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.wasDragging = true;
      }
    }
  }

  onDragEnd(event) {
    if (!this.draggedSprite) return;
    // Update quadtree with new position
    this.updateQuadtree();
    this.draggedSprite.cursor = 'pointer';
    this.draggedSprite = null;
  }

  navigateTo(url) {
    // Use history.pushState to update URL without hard refresh
    // Then manually trigger Highway navigation
    history.pushState({}, "", url);
    
    // Manually trigger Highway navigation by dispatching popstate
    // Highway should handle this and load the new page content
    window.dispatchEvent(new PopStateEvent("popstate", { state: {} }));
  }

  updateQuadtree() {
    // Rebuild quadtree with current sprite positions
    this.quadtree.clear();
    this.sprites.forEach(sprite => {
      this.quadtree.insert({
        x: sprite.x - sprite.width / 2,
        y: sprite.y - sprite.height / 2,
        width: sprite.width,
        height: sprite.height,
        sprite,
      });
    });
  }

  handleResize() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this._recomputeLayout();
    }, 250);
  }

  _recomputeLayout() {
    if (!this.app || !this.container) return;
    // Resize renderer
    this.app.renderer.resize(this.el.offsetWidth, this.el.offsetHeight);
    // Update image sizer
    this.imageSizer.updateScreenWidth(window.innerWidth);
    // Re-layout all sprites by reloading images for new random positions
    if (!this.imgElements || !this.imgElements.length) return;
    // Destroy current sprites
    if (this.sprites) {
      this.sprites.forEach(sprite => {
        if (sprite.texture) sprite.texture.destroy(true);
        sprite.destroy({ children: true });
      });
    }
    this.sprites = [];
    this.container.removeChildren();
    this.quadtree.clear();
    // Reload images (randomizes positions)
    this.loadImages(this.imgElements);
  }

  computeAllPositions(count, w, h, sizes) {
    return {
      edge: this.edgeFirstPositions(count, w, h, sizes),
      scattered: this.scatteredPositions(count, w, h, sizes),
    };
  }

  applySpritePositions(sprites, positions, startZ = 0) {
    sprites.forEach((sprite, i) => {
      const pos = positions[i];
      sprite.x = pos.x;
      sprite.y = pos.y;
      sprite.zIndex = startZ + i;
    });
  }

  destroy() {
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    window.removeEventListener('resize', this.handleResize);
    if (this.sprites) {
      this.sprites.forEach(sprite => {
        if (sprite.texture) sprite.texture.destroy(true);
        sprite.destroy({ children: true });
      });
    }
    if (this.container) this.container.destroy({ children: true });
    if (this.app) this.app.destroy(true, true);
    this.sprites = [];
    this.quadtree = null;
    this.quadtreeBounds = null;
    super.destroy();
  }
}
